import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";
import { clamp, isNumber, range } from "../../utils/utils";
import { ButtonSize, ButtonVariant } from "../../utils/button.enum";

const baseClass = "ux-pagination";

@Component({
    tag: "ux-pagination",
    styleUrl: "pagination.scss"
})
export class Pagination {
    // Space required for ellipsis and active page
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private static readonly RESERVED_SPACE: number = 3;

    private observer!: ResizeObserver;
    private desiredBoundaryCount!: number;
    private desiredSiblingCount!: number;
    private buttonWidth = 60; // largest current button size, only as default

    @Element() host!: HTMLUxPaginationElement;

    /** The total page count */
    @Prop() readonly totalPages!: number;

    /** The current page, in the range of 1 - total-pages */
    @Prop({ mutable: true, reflect: true }) page = 1;

    /**
     * The waiting time after input changes (mobile layout and ellipsis inputs)
     * before the page is updated
     */
    @Prop() readonly inputDebounceMs: number = 750;

    /** The maximum visible page count, useful to force narrow layout */
    @Prop() readonly maxVisible: number = 7;

    /**
     * Number of always visible pages at the beginning and end.
     * If not set, will be calculated based on `maxVisible` ((maxVisible - 3) / 4).
     * May overwrite max-visible.
     */
    @Prop() readonly boundaryCount!: number;

    /**
     * Number of always visible pages before and after the current one.
     * If not set, will be calculated based on `maxVisible` ((maxVisible - 3) / 4).
     * May overwrite max-visible.
     */
    @Prop() readonly siblingCount!: number;

    @State() width!: number;

    @Watch("totalPages")
    validateRequiredProps(value: number) {
        const isBlank = !isNumber(value) || value <= 0;
        if (isBlank) {
            throw new Error("total-pages is required and has to be greater than 0");
        }
    }

    @Watch("maxVisible")
    @Watch("boundaryCount")
    @Watch("siblingCount")
    updateDesiredCounts() {
        const fourthOfAvailableSpace = Math.floor((this.maxVisible - Pagination.RESERVED_SPACE) / 4);

        this.desiredBoundaryCount = this.boundaryCount !== undefined ? this.boundaryCount : fourthOfAvailableSpace;
        this.desiredSiblingCount = this.siblingCount !== undefined ? this.siblingCount : fourthOfAvailableSpace;
    }

    /** Emitted on page changes */
    @Event({ eventName: "uxChange" }) change!: EventEmitter<{ page: number }>;

    private debouncedHandleInputChanges!: (event: CustomEvent) => void;

    connectedCallback() {
        if (!window.ResizeObserver) {
            return;
        }

        this.observer = new ResizeObserver((entries) => {
            this.updateButtonWidth();

            const entry = entries[0];
            if (entry.contentBoxSize) {
                const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
                this.width = contentBoxSize.inlineSize;
            } else {
                this.width = entry.contentRect.width;
            }
        });
    }

    componentWillLoad() {
        this.updateDesiredCounts();

        if (!window.ResizeObserver) {
            setTimeout(() => {
                this.updateButtonWidth();
                this.width = this.host.getBoundingClientRect().width;
            });
        } else {
            this.observer.observe(this.host);
        }

        let timer: ReturnType<typeof setTimeout>;
        this.debouncedHandleInputChanges = (event: CustomEvent) => {
            clearTimeout(timer);
            timer = setTimeout(() => this.handleInputChanges(event), this.inputDebounceMs);
        };
    }

    private updateButtonWidth() {
        const buttonWidth = parseInt(
            getComputedStyle(this.host).getPropertyValue("--ux-pagination-button-width").replace("px", ""),
            10
        );

        if (isNumber(buttonWidth) && buttonWidth > 0) {
            this.buttonWidth = buttonWidth;
        }
    }

    private handleKeydown(event: KeyboardEvent) {
        event.stopPropagation();

        if (event.key === "Enter") {
            // prevent click event on ellipsis button focused after closing
            event.preventDefault();

            const numberInput = (event.target as HTMLInputElement).closest("ux-input-field-number");
            // @ts-expect-error Check for typing
            this.handleInput(numberInput);
        }
    }

    private handleInputChanges(event: CustomEvent) {
        if (!event.target) {
            return;
        }

        const target = event.target as HTMLUxInputFieldNumberElement;
        this.handleInput(target);
    }

    private handlePopoverHidden(event: CustomEvent) {
        if (!event.target) {
            return;
        }

        const numberInput = (event.target as HTMLUxPopoverElement).querySelector("ux-input-field-number");
        // @ts-expect-error Check for typing
        this.handleInput(numberInput);
    }

    private handleInput(input: HTMLUxInputFieldNumberElement) {
        if (!input) {
            return;
        }

        if (isNaN(input.value)) {
            return;
        }

        this.moveToPage(input.value);
    }

    private moveToPage(p: number) {
        const prev = this.page;
        this.page = clamp(p, 1, this.totalPages);

        if (prev !== this.page) {
            this.change.emit({ page: this.page });
        }
    }

    private renderMobilePageInput() {
        return (
            <ux-input-field-number
                defaultValue={1}
                min={1}
                max={this.totalPages}
                unit={this.totalPages.toString()}
                value={this.page}
                onUxChange={(e) => this.debouncedHandleInputChanges(e)}
                onKeyDown={(e) => this.handleKeydown(e)}
            />
        );
    }

    private renderPageButtons(count: number, startAt: number = 1) {
        return (
            <div class={`${baseClass}__button-group`}>
                {range(count, startAt).map((p) => (
                    <ux-button class={`${baseClass}__page-button`} selected={p === this.page} onClick={() => this.moveToPage(p)}>
                        {p}
                    </ux-button>
                ))}
            </div>
        );
    }

    private handlePopoverShow = (event: Event) => {
        const input = (event.target as HTMLElement).querySelector<HTMLUxInputFieldNumberElement>("ux-input-field-number");
        if (input) {
            setTimeout(() => input.setFocus(), 100);
        }
    };

    private renderEllipsis(position: "left" | "right") {
        const placement = position === "left" ? "top-start" : "top-end";

        return (
            <ux-popover
                placement={placement}
                onUxHide={(e) => this.handlePopoverHidden(e)}
                onUxShow={(e) => this.handlePopoverShow(e)}
            >
                <ux-button class={`${baseClass}__ellipsis`} icon="ellipsis" slot="trigger" variant={ButtonVariant.NoBorder} />
                <ux-popover-panel>{this.renderMobilePageInput()}</ux-popover-panel>
            </ux-popover>
        );
    }

    private renderPartialPages(availableSpace: number) {
        const remainingSpace = availableSpace - Pagination.RESERVED_SPACE;
        const desiredTotal = Math.max(1, this.desiredBoundaryCount + this.desiredSiblingCount);
        const boundaryFraction = this.desiredBoundaryCount / desiredTotal;
        const boundaryCount = Math.min(this.desiredBoundaryCount, Math.ceil((remainingSpace * boundaryFraction) / 2));
        const siblingCount = Math.min(this.desiredSiblingCount, Math.floor((remainingSpace - boundaryCount * 2) / 2));
        const boundaryLimit = boundaryCount + siblingCount * 2 + 2;

        if (this.page <= boundaryLimit - siblingCount) {
            return [
                this.renderPageButtons(boundaryLimit, 1),
                this.renderEllipsis("right"),
                this.renderPageButtons(boundaryCount, this.totalPages - boundaryCount + 1)
            ];
        }

        if (this.page > this.totalPages - (boundaryLimit - siblingCount)) {
            return [
                this.renderPageButtons(boundaryCount, 1),
                this.renderEllipsis("left"),
                this.renderPageButtons(boundaryLimit, this.totalPages - boundaryLimit + 1)
            ];
        }

        return [
            this.renderPageButtons(boundaryCount, 1),
            this.renderEllipsis("left"),
            this.renderPageButtons(siblingCount * 2 + 1, this.page - siblingCount),
            this.renderEllipsis("right"),
            this.renderPageButtons(boundaryCount, this.totalPages - boundaryCount + 1)
        ];
    }

    private renderPages() {
        const calculatedSpace = this.desiredBoundaryCount * 2 + this.desiredSiblingCount * 2 + Pagination.RESERVED_SPACE;
        const availableSpace = Math.min(Math.floor(this.width / this.buttonWidth) - 2, Math.max(this.maxVisible, calculatedSpace));

        if (availableSpace < Pagination.RESERVED_SPACE + 2) {
            return this.renderMobilePageInput();
        }

        if (this.totalPages <= availableSpace) {
            return this.renderPageButtons(this.totalPages);
        }

        return this.renderPartialPages(availableSpace);
    }

    render() {
        if (!this.width) {
            return;
        }

        return (
            <Host>
                <ux-button
                    disabled={this.page === 1}
                    size={ButtonSize.Icon}
                    icon="chevron-left"
                    onClick={() => this.moveToPage(this.page - 1)}
                />
                <div class={`${baseClass}__inner`}>{this.renderPages()}</div>
                <ux-button
                    disabled={this.page === this.totalPages}
                    size={ButtonSize.Icon}
                    icon="chevron-right"
                    onClick={() => this.moveToPage(this.page + 1)}
                />
            </Host>
        );
    }
}
